import React, { useState } from "react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, PlayCircle, Flag } from "lucide-react";
import { DogItemActions } from "@/components/DogItemActions";
import { DogItemContent } from "@/components/DogItemContent";
import { EditDogForm } from "@/components/EditDogForm";
import { getWeekdayFromDateKey, getWeekStartDateKey } from "@/lib/dateUtils";

import { makeDogStateKey } from "@/lib/dogIdentity";

export function DogListItem({
  dog,
  index,
  selectedDayKey,
  completedDogs,
  urgentDogs,
  daysList,
  onDogClick,
  onUrgentClick,
  onDelete,
  onEdit,
  currentDate,
  getInitialDayData,
  pendingConfirmation,
  onConfirmAction,
  onCancelAction,
  requireConfirmation = false,
  onApplyCanceledDaysBatch,
  isReadOnly
}) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);

  React.useEffect(() => {
    if (isReadOnly) {
      setIsEditDialogOpen(false);
      setIsScheduleDialogOpen(false);
    }
  }, [isReadOnly]);

  const sortableId = dog.id || dog.name;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 50 : undefined,
  };

  const handleItemClick = () => {
    if (isDragging || isReadOnly) return;
    console.error('[TAP_LOCAL]', JSON.stringify({ n: dog.name, k: makeDogStateKey(selectedDayKey, dog), t: Date.now() }));
    onDogClick(dog.name, dog.id);
  };

  const dogKey = makeDogStateKey(selectedDayKey, dog);
  const isCompleted = completedDogs[dogKey];
  const isUrgent = urgentDogs[dogKey];

  const isPending = pendingConfirmation
    ? (dog.id
        ? pendingConfirmation.dogId === dog.id
        : (!pendingConfirmation.dogId && pendingConfirmation.dogName === dog.name))
    : false;
  const pendingIsCurrentlyCompleted = isPending ? pendingConfirmation.isCurrentlyCompleted : false;
  const pendingType = isPending ? pendingConfirmation.type : undefined;
  const pendingIsCurrentlyUrgent = isPending ? (pendingConfirmation.isCurrentlyUrgent || false) : false;

  const currentDayDogData = (daysList?.[`${selectedDayKey}`] || []).find(
    (d) => d.name === dog.name
  );
  const hasNoteForCurrentDay = !!(
    currentDayDogData?.informativeNote &&
    currentDayDogData.informativeNote.trim() !== ""
  );

  const scheduleForToday = (() => {
    if (!dog.schedules || dog.schedules.length === 0) return null;
    if (!selectedDayKey) return null;
    try {
      const weekday = getWeekdayFromDateKey(selectedDayKey);
      const weekStartKey = getWeekStartDateKey(selectedDayKey);

      const punctual = dog.schedules.find(s =>
        (s.onlyThisWeek ?? false) &&
        s.weekStartDateKey === weekStartKey &&
        Array.isArray(s.days) &&
        s.days.includes(weekday)
      );
      if (punctual) return punctual;

      return dog.schedules.find(s =>
        !(s.onlyThisWeek ?? false) &&
        Array.isArray(s.days) &&
        s.days.includes(weekday)
      ) || null;
    } catch {
      return null;
    }
  })();

  let itemBgClass = "bg-card hover:bg-secondary border border-border";
  if (isPending) {
    itemBgClass = "bg-amber-50 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-600";
  } else if (isCompleted) {
    itemBgClass =
      "bg-primary/40 text-foreground border border-primary hover:bg-primary/50";
  } else if (isUrgent) {
    itemBgClass =
      "bg-red-500/20 text-foreground border border-border hover:bg-red-500/30";
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="dog-item bg-card p-0 rounded-lg select-none"
      aria-label={`Perro: ${dog.name}`}
    >
      <div
        className={`w-full flex flex-col text-left rounded-md transition-colors duration-200 ${itemBgClass}`}
      >
        <div className="flex items-center justify-between py-1 px-3">
          {!isReadOnly && (
            <div
              {...listeners}
              {...attributes}
              style={{ touchAction: "none" }}
              className="cursor-grab h-8 w-8 text-muted-foreground hover:text-foreground flex items-center justify-center flex-shrink-0"
              aria-label="Arrastrar para reordenar"
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}

          <DogItemContent
            dog={dog}
            isCompleted={isCompleted}
            handleItemClick={handleItemClick}
            hasNote={hasNoteForCurrentDay}
            isPending={isPending}
            pendingIsCurrentlyCompleted={pendingIsCurrentlyCompleted}
            pendingType={pendingType}
            pendingIsCurrentlyUrgent={pendingIsCurrentlyUrgent}
            onConfirmAction={onConfirmAction}
            onCancelAction={onCancelAction}
          />

          {!isPending && !isReadOnly && (
            <DogItemActions
              dog={dog}
              selectedDayKey={selectedDayKey}
              onEdit={onEdit}
              onEditClick={() => setIsEditDialogOpen(true)}
              onScheduleClick={() => setIsScheduleDialogOpen(true)}
              daysList={daysList}
              currentDate={currentDate}
              getInitialDayData={getInitialDayData}
              isUrgent={isUrgent}
              onUrgentClick={onUrgentClick}
              isReadOnly={isReadOnly}
            />
          )}

        </div>

        {!isPending && scheduleForToday && (scheduleForToday.arrivalTime || scheduleForToday.departureTime) && (
          <div className="flex items-center justify-end gap-3 px-3 pb-2 select-none whitespace-nowrap">
            <div className="flex items-center gap-1">
              {scheduleForToday.arrivalTime ? (
                <>
                  <PlayCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="bg-slate-200 dark:bg-slate-700 text-slate-950 dark:text-slate-100 border border-slate-400/40 px-2 py-0.5 rounded font-mono text-xs">
                    {scheduleForToday.arrivalTime}
                  </span>
                </>
              ) : (
                <span className="invisible pointer-events-none flex items-center gap-1" aria-hidden="true">
                  <PlayCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="px-2 py-0.5 rounded font-mono text-xs border">00:00</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {scheduleForToday.departureTime ? (
                <>
                  <Flag className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="bg-slate-200 dark:bg-slate-700 text-slate-950 dark:text-slate-100 border border-slate-400/40 px-2 py-0.5 rounded font-mono text-xs">
                    {scheduleForToday.departureTime}
                  </span>
                </>
              ) : (
                <span className="invisible pointer-events-none flex items-center gap-1" aria-hidden="true">
                  <Flag className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="px-2 py-0.5 rounded font-mono text-xs border">00:00</span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {isEditDialogOpen && (
        <EditDogForm
          mode="full"
          dog={dog}
          selectedDayKey={selectedDayKey}
          onEdit={onEdit}
          onDelete={onDelete}
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          daysList={daysList}
          currentDate={currentDate}
          getInitialDayData={getInitialDayData}
          triggerButton={null}
          urgentDogs={urgentDogs}
          requireConfirmation={requireConfirmation}
          onUrgentClick={onUrgentClick}
          onApplyCanceledDaysBatch={onApplyCanceledDaysBatch}
        />
      )}

      {isScheduleDialogOpen && (
        <EditDogForm
          mode="scheduleOnly"
          dog={dog}
          selectedDayKey={selectedDayKey}
          onEdit={onEdit}
          isOpen={isScheduleDialogOpen}
          onOpenChange={setIsScheduleDialogOpen}
          daysList={daysList}
          currentDate={currentDate}
          getInitialDayData={getInitialDayData}
          triggerButton={null}
          urgentDogs={urgentDogs}
          requireConfirmation={requireConfirmation}
          onUrgentClick={onUrgentClick}
          onApplyCanceledDaysBatch={onApplyCanceledDaysBatch}
        />
      )}

    </div>
  );
}
